/**
 * The greeting with its NAME CLAUSE removed, for an account whose name has not resolved.
 *
 * The three `home.greeting.*` strings bake the name in, and every locale attaches its own furniture
 * to it. Dropping only a trailing comma — which is what the home used to do — leaves the furniture
 * behind: Japanese rendered `おはようございます、さん` and Korean `좋은 아침이에요, 님`, greeting the
 * reader as "Mr." with nobody's name in front of it.
 *
 * So the whole clause goes: the placeholder, the honorific fused to it, and the separator on
 * whichever side it sits. The rule is locale-AGNOSTIC on purpose — a list of honorifics is a list
 * that goes stale the next time a bundle is translated, and there are 37 of them.
 *
 * - The honorific run stops at the next space or separator, so a locale that puts the name FIRST
 *   (`{firstName}さん、おはよう`) loses the clause and keeps its greeting, rather than having the rest
 *   of the sentence eaten.
 * - Any separator left stranded at either end goes with it.
 *
 * `translate` is `t`, called with a SENTINEL in place of the name — the raw template is not reachable
 * through the i18n hook, and interpolating a marker is what makes the clause findable afterwards.
 */
const SENTINEL = '\u0000';

/**
 * Every character that appears immediately before `{firstName}` in any of the 37 bundles: ASCII
 * comma, ARABIC comma (U+060C), ideographic comma, full-width comma. The Arabic one was missing and
 * left `صباح الخير،` with its punctuation dangling (review) — and the first version of the guard
 * could not catch that, because it asserted against this same set and so passed vacuously. The guard
 * now checks for trailing punctuation of ANY kind, derived independently of this list.
 *
 * Thai puts the name after a plain space with no separator at all, which `\s*` already covers.
 */
const SEPARATORS = ',\u060C，、';

const NAME_CLAUSE = new RegExp(`[${SEPARATORS}]?\\s*${SENTINEL}[^\\s${SEPARATORS}]*`, 'u');
const STRANDED = new RegExp(`^[\\s${SEPARATORS}]+|[\\s${SEPARATORS}]+$`, 'gu');

export function greetingWithoutName(translate: (key: string, vars: Record<string, string>) => string, key: string): string {
  return translate(key, { firstName: SENTINEL }).replace(NAME_CLAUSE, '').replace(STRANDED, '').trim();
}

/** The separator (comma / locale comma) sitting DIRECTLY before the name, with any space after it. */
const SEPARATOR_BEFORE_NAME = new RegExp(`[${SEPARATORS}](\\s*)(?=${SENTINEL})`, 'u');

/**
 * The greeting with the comma between it and the name removed, but the NAME kept — "Good morning, Caro"
 * → "Good morning Caro" (Figma 21639-650906). Only the separator sitting directly before the name
 * placeholder is dropped (its following space is preserved), so a name-FIRST locale
 * (`{firstName}さん、おはよう`) keeps its own sentence punctuation, which is not that separator.
 */
export function greetingWithoutComma(
  translate: (key: string, vars: Record<string, string>) => string,
  key: string,
  name: string,
): string {
  // `() => name` inserts the name LITERALLY: a replacement string would interpret `$&`/`$1`/`` $` `` in a
  // name (e.g. a name of "$&" would re-insert the sentinel).
  return translate(key, { firstName: SENTINEL }).replace(SEPARATOR_BEFORE_NAME, '$1').replace(SENTINEL, () => name);
}

/**
 * Whether the locale puts the name FIRST (`{firstName}さん、おはようございます`). Japanese does; every
 * other bundle greets first and names last, and keeps the home's long-standing no-name handling.
 */
export function greetingLeadsWithName(translate: (key: string, vars: Record<string, string>) => string, key: string): boolean {
  return translate(key, { firstName: SENTINEL }).trimStart().startsWith(SENTINEL);
}

/** Exported for the guard that runs this over all 37 bundles. */
export const GREETING_NAME_SENTINEL = SENTINEL;
